import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../hooks/useAuditoriaInteligente';
import { ICompany } from '../../../hooks/useCompanies';
import { IAuditoriaEmpresa, Escopo, AuditoriaArea } from './types';
import { IPeriodo } from './components/ContextBar';

/**
 * Contexto compartilhado do módulo Auditoria Inteligente, 100% na URL (mesmo
 * padrão do `useFinanceiroFilters`): empresa selecionada (id), escopo
 * (carteira/empresa) e período. A empresa é reidratada por react-query (keyed
 * pelo id, idempotente e cacheado entre rotas), então este hook pode ser
 * chamado em mais de um componente da mesma página sem divergir.
 *
 * Cada submódulo é uma ROTA própria (`/auditoria-inteligente/<sub>`); o estado
 * vive na querystring e persiste ao navegar entre submódulos pela sidebar.
 */

export const AUDITORIA_BASE = '/modulesHome/auditoria-inteligente';

const toEmpresa = (c: ICompany): IAuditoriaEmpresa => ({
  id: c.id,
  external_id: c.external_id,
  nome: c.nome || c.razao_social,
  razao_social: c.razao_social,
  cnpj: c.cnpj,
  uf: c.uf,
  cidade: c.cidade,
  regime: c.regime_tributario_atualizado,
});

/** screen key (usado pelas telas) -> segmento de rota do submódulo. */
const SCREEN_TO_SEG: Record<string, string> = {
  impApurados: 'impostos-apurados',
  impNota: 'impostos-por-nota',
  clientesForn: 'clientes-fornecedores',
};
const seg = (screen: string) => SCREEN_TO_SEG[screen] ?? screen;

export interface AuditoriaContext {
  empresa: IAuditoriaEmpresa | null;
  empresaId: number | null;
  escopo: Escopo;
  periodo: IPeriodo;
  periodoParams: IPeriodoParams;
  /** Carteira: só clientes vigentes no período (dt_cli_desde/ate) em vez do status cadastral. */
  somenteAtivas: boolean;
  /** Período + o flag `somente_ativas` — params das telas de carteira. */
  carteiraParams: IPeriodoParams & { somente_ativas?: 1 };
  loadingEmpresa: boolean;
  onSelectEmpresa: (e: IAuditoriaEmpresa) => void;
  onClearEmpresa: () => void;
  onEscopo: (e: Escopo) => void;
  onPeriodo: (p: IPeriodo) => void;
  onToggleAtivas: (v: boolean) => void;
  /** Drill por id (rankings): abre o Dashboard da empresa. */
  onOpenEmpresaById: (id: number) => void;
  /** Drill por id para um submódulo específico (ex.: ranking → Dossiê 360). */
  onOpenEmpresaScreen: (id: number, screen: string) => void;
  /** Drill de Cruzamentos: abre o Painel de Auditorias filtrado pela área e,
   *  opcionalmente, com foco numa linha (key = `${area}-${regra}-${periodo}`). */
  onOpenAuditoria: (area: AuditoriaArea, focusKey?: string) => void;
  /** Navegação genérica entre submódulos (ex.: KPI → tela). */
  onScreen: (screen: string) => void;
  /** Drill: abre Notas Fiscais filtrado por um parceiro (CNPJ/nome) e tipo de nota. */
  onOpenNotas: (busca: string, tipo?: 'saida' | 'entrada' | 'servico') => void;
  /** Drill: abre Notas Fiscais com o período fixado num mês (mes = 'YYYY-MM' ou 'YYYY-MM-DD'). */
  onOpenNotasMes: (mes: string) => void;
}

export function useAuditoriaContext(): AuditoriaContext {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { getEmpresaById } = useAuditoriaInteligente();
  const toast = useToast();

  const empresaId = sp.get('empresa') ? Number(sp.get('empresa')) : null;
  const escopo = (sp.get('escopo') as Escopo) || 'carteira';
  const ini = sp.get('ini') || moment().subtract(5, 'months').format('YYYY-MM');
  const fim = sp.get('fim') || moment().format('YYYY-MM');
  // Ligado por padrao: só clientes ativos (ativa='Ativa'). Desligar grava `ativas=0`.
  const somenteAtivas = sp.get('ativas') !== '0';

  const { data: hydrated, error: hydrateError } = useQuery<ICompany | null>({
    queryKey: ['ai-empresa-by-id', empresaId],
    queryFn: getEmpresaById,
    enabled: !!empresaId,
    retry: false,
  });
  const empresa = hydrated ? toEmpresa(hydrated) : null;
  const loadingEmpresa = empresaId != null && empresa?.id !== empresaId;

  const patch = (obj: Record<string, string | null>) =>
    setSp(
      prev => {
        const next = new URLSearchParams(prev);
        Object.entries(obj).forEach(([k, v]) => (v === null ? next.delete(k) : next.set(k, v)));
        return next;
      },
      { replace: true },
    );

  // Empresa via URL fora do escopo do usuário → 403: avisa e limpa a seleção.
  useEffect(() => {
    const status = (hydrateError as AxiosError)?.response?.status;
    if (status === 403) {
      toast({
        id: 'ai-empresa-403',
        title: 'Sem permissão para esta empresa',
        description: 'Você não tem acesso a esta empresa. Selecione outra na busca.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      patch({ empresa: null, escopo: 'empresa' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateError]);

  const periodo: IPeriodo = { ini, fim };
  const periodoParams: IPeriodoParams = {
    data_ini: `${ini}-01`,
    data_fim: moment(`${fim}-01`).endOf('month').format('YYYY-MM-DD'),
  };
  const carteiraParams: IPeriodoParams & { somente_ativas?: 1 } = {
    ...periodoParams,
    ...(somenteAtivas ? { somente_ativas: 1 } : {}),
  };

  const qs = (extra: Record<string, string>) => {
    const p = new URLSearchParams();
    if (empresaId) {
      p.set('empresa', String(empresaId));
      p.set('escopo', 'empresa');
    }
    p.set('ini', ini);
    p.set('fim', fim);
    if (!somenteAtivas) p.set('ativas', '0');
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  };

  return {
    empresa,
    empresaId,
    escopo,
    periodo,
    periodoParams,
    somenteAtivas,
    carteiraParams,
    loadingEmpresa,
    onSelectEmpresa: e => patch({ empresa: String(e.id), escopo: 'empresa' }),
    onClearEmpresa: () => patch({ empresa: null }),
    onEscopo: e => patch({ escopo: e }),
    onPeriodo: p => patch({ ini: p.ini, fim: p.fim }),
    onToggleAtivas: v => patch({ ativas: v ? null : '0' }),
    onOpenEmpresaById: id =>
      navigate(`${AUDITORIA_BASE}/dashboard?empresa=${id}&escopo=empresa&ini=${ini}&fim=${fim}`),
    onOpenEmpresaScreen: (id, screen) =>
      navigate(
        `${AUDITORIA_BASE}/${seg(screen)}?empresa=${id}&escopo=empresa&ini=${ini}&fim=${fim}`,
      ),
    onOpenAuditoria: (area, focusKey) =>
      navigate(
        `${AUDITORIA_BASE}/auditorias?${qs(focusKey ? { area, focus: focusKey } : { area })}`,
      ),
    onScreen: screen => navigate(`${AUDITORIA_BASE}/${seg(screen)}?${qs({})}`),
    onOpenNotas: (busca, tipo) =>
      navigate(`${AUDITORIA_BASE}/notas?${qs(tipo ? { busca, tipo } : { busca })}`),
    onOpenNotasMes: mes => {
      // mes 'YYYY-MM[-DD]' → fixa ini=fim=mês (o extra do qs sobrescreve o período global).
      const m = mes.slice(0, 7);
      navigate(`${AUDITORIA_BASE}/notas?${qs({ ini: m, fim: m })}`);
    },
  };
}
